'use client';

// src/app/login/page.tsx
//
// Thème couverture (03/10/2026) : la page de connexion reprend la langue du site — à
// gauche l'aplat bleu et la promesse, à droite le formulaire sur le papier. Le faisceau de
// « lumière noire » (canevas animé, violets) appartenait aux anciens thèmes : retiré, et
// avec lui le titre devenu illisible (couleur de la coque sombre posée sur un fond noir).
//
// La logique est inchangée : callbackUrl limité aux chemins internes, connexion par
// identifiants, inscription qui crée un compte EN ATTENTE de validation.

import { useState, Suspense } from 'react';
import { signIn } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import Link from '@/components/i18n/Link';
import { FiligraneLoupe } from '@/components/ds/FiligraneLoupe';

import { useLang, useT } from '@/components/i18n/LangProvider';
import { localiser } from '@/lib/i18n/config';
const DISP = 'var(--font-display)';
const DATA = 'var(--font-data)';
const EDIT = 'var(--font-editorial)';

/** Le logo typographique de la barre de navigation. */
function Logo({ sur = 'papier' }: { sur?: 'papier' | 'bleu' }) {
  return (
    <Link href="/" style={{ fontFamily: DISP, fontSize: 22, fontWeight: 800, letterSpacing: '-0.03em', textDecoration: 'none', color: sur === 'bleu' ? '#ffffff' : 'var(--text-primary)' }}>
      lumière <span style={{ color: sur === 'bleu' ? 'rgba(255,255,255,0.72)' : 'var(--color-uv)' }}>noire</span>
    </Link>
  );
}

function LoginContent() {
  const t = useT();
  const searchParams = useSearchParams();
  const lang = useLang();

  const rawCallback = searchParams.get('callbackUrl') || '/';
  const callbackUrl = (() => {
    try {
      return new URL(rawCallback).pathname;
    } catch {
      return rawCallback.startsWith('/') ? rawCallback : '/';
    }
  })();

  const [mode, setMode] = useState<'login' | 'register'>(
    searchParams.get('mode') === 'register' ? 'register' : 'login'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [registered, setRegistered] = useState(false);

  function parseLoginError(err: string): string {
    if (err.includes('ACCOUNT_PENDING')) return t('login.enAttente');
    if (err.includes('ACCOUNT_DISABLED')) return t('login.desactive');
    if (err.includes('RATE_LIMITED')) return t('login.tropTentatives');
    return t('login.incorrect');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (mode === 'login') {
      const res = await signIn('credentials', {
        email,
        password,
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
        redirect: false,
      });
      if (res?.error) {
        setError(parseLoginError(res.error));
      } else {
        window.location.href = localiser(callbackUrl, lang);
      }
    } else {
      try {
        const res = await fetch('/api/auth/register', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, name }),
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.error || t('login.erreurCreation'));
        } else {
          setRegistered(true);
        }
      } catch {
        setError(t('login.erreurReseau'));
      }
    }
    setLoading(false);
  }

  const champ: React.CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '12px 14px', borderRadius: 0,
    background: 'var(--color-surface)', color: 'var(--text-primary)',
    border: 'none', boxShadow: 'inset 0 0 0 1.5px var(--text-primary)',
    fontFamily: DATA, fontSize: 15, outline: 'none',
  };
  const etiquette: React.CSSProperties = {
    display: 'block', marginBottom: 6, fontFamily: DATA, fontSize: 12, fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-secondary)',
  };

  return (
    <div className="login-grille" style={{ minHeight: '100vh', background: 'var(--color-surface)', fontFamily: DATA }}>

      {/* ── À gauche : la couverture ───────────────────────────────────── */}
      <section className="couv login-couv" aria-label="Lumière Noire">
        {/* Le filigrane du slogan (05/10/2026) : la loupe en blanc très pâle, dans le vide
            au-dessus de la promesse. Premier enfant : le texte, positionné après, passe dessus. */}
        <FiligraneLoupe style={{ position: 'absolute', left: '-6%', top: '6%', width: '112%', height: '50%', color: '#ffffff', opacity: 0.11 }} />
        <Logo sur="bleu" />
        <div style={{ flex: 1 }} />
        <h2 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(44px, 5.4vw, 84px)', letterSpacing: '-0.05em', lineHeight: 0.9, margin: 0, color: '#ffffff' }}>
          {t('login.devise')}
        </h2>
        <p style={{ fontFamily: EDIT, fontSize: 18, lineHeight: 1.55, color: 'rgba(255,255,255,0.88)', margin: '20px 0 0', maxWidth: '44ch' }}>
          {t('login.promesse')}
        </p>
        <ul style={{ listStyle: 'none', margin: '28px 0 0', padding: 0, display: 'grid', gap: 0, maxWidth: 440 }}>
          {[
            [t('login.parolesTitre'), t('login.parolesTexte')],
            ['RayonX', t('login.rayonxTexte')],
            [t('login.reseauxTitre'), t('login.reseauxTexte')],
          ].map(([titre, texte]) => (
            <li key={titre} style={{ borderTop: '1px solid rgba(255,255,255,0.35)', padding: '10px 0', fontFamily: DATA, fontSize: 14, color: 'rgba(255,255,255,0.85)' }}>
              <strong style={{ color: '#ffffff', fontWeight: 800 }}>{titre}</strong> — {texte}
            </li>
          ))}
        </ul>
      </section>

      {/* ── À droite : le formulaire, sur le papier ────────────────────── */}
      <main className="login-form">
        <div className="login-logo-mobile"><Logo /></div>

        {registered ? (
          <div>
            <span style={{ ...etiquette, color: 'var(--color-uv)' }}>{t('login.inscription')}</span>
            <h1 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 36, letterSpacing: '-0.035em', lineHeight: 1, margin: '0 0 14px', color: 'var(--text-primary)' }}>
              {t('login.demandeEnregistree')}
            </h1>
            {/* Dire ce qui se passe vraiment. Cet écran annonçait « Votre compte est actif.
                Connectez-vous maintenant » alors que les accès se donnent à la main : depuis le
                23 septembre 2026 l'inscription crée un compte en attente, et une connexion
                immédiate se solderait par un refus incompréhensible. */}
            <p style={{ fontFamily: EDIT, fontSize: 17, lineHeight: 1.6, color: 'var(--text-secondary)', margin: '0 0 24px' }}>
              {t('login.seraActive')}
            </p>
            <button
              onClick={() => { setRegistered(false); setMode('login'); setPassword(''); setName(''); }}
              className="press"
              style={{ padding: '12px 22px', borderRadius: 999, border: 'none', background: 'var(--color-uv)', color: '#ffffff', fontFamily: DATA, fontSize: 14, fontWeight: 800, cursor: 'pointer' }}
            >
              {t('login.retourConnexion')}
            </button>
          </div>
        ) : (
          <>
            {/* Connexion / Inscription : des mots soulignés, comme les réponses du quiz. */}
            <div role="tablist" style={{ display: 'flex', gap: 24, marginBottom: 28 }}>
              {(['login', 'register'] as const).map((m) => {
                const actif = mode === m;
                return (
                  <button
                    key={m}
                    role="tab"
                    aria-selected={actif}
                    onClick={() => { setMode(m); setError(''); }}
                    className="reponse-couv"
                    style={{
                      fontFamily: DISP, fontSize: 17, fontWeight: 800, padding: '4px 0 3px', cursor: 'pointer',
                      background: 'transparent', border: 'none', borderRadius: 0, color: 'var(--text-primary)',
                      borderBottom: actif ? '3px solid var(--text-primary)' : '2px solid var(--border-default)',
                      opacity: actif ? 1 : 0.45,
                    }}
                  >
                    {t(m === 'login' ? 'login.connexion' : 'login.inscription')}
                  </button>
                );
              })}
            </div>

            <h1 style={{ fontFamily: DISP, fontWeight: 800, fontSize: 'clamp(32px, 3vw, 42px)', letterSpacing: '-0.035em', lineHeight: 1, margin: '0 0 10px', color: 'var(--text-primary)' }}>
              {t(mode === 'login' ? 'login.bonRetour' : 'login.ouvrirCompte')}
            </h1>
            <p style={{ fontFamily: EDIT, fontSize: 17, lineHeight: 1.55, color: 'var(--text-secondary)', margin: '0 0 28px' }}>
              {t(mode === 'login' ? 'login.sousConnexion' : 'login.sousInscription')}
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {mode === 'register' && (
                <div>
                  <label style={etiquette} htmlFor="login-nom">{t('login.nom')}</label>
                  <input id="login-nom" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('login.votreNom')} required className="login-champ" style={champ} />
                </div>
              )}

              <div>
                <label style={etiquette} htmlFor="login-email">{t('login.email')}</label>
                <input id="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t('login.emailExemple')} required autoComplete="email" className="login-champ" style={champ} />
              </div>

              <div>
                <label style={etiquette} htmlFor="login-mdp">{t('login.motDePasse')}</label>
                <input
                  id="login-mdp" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'} className="login-champ" style={champ}
                />
              </div>

              {error && (
                <div role="alert" style={{ borderLeft: '3px solid var(--color-danger)', padding: '6px 0 6px 12px', fontFamily: DATA, fontSize: 14, color: 'var(--text-primary)' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="press"
                style={{
                  marginTop: 6, padding: '14px 20px', borderRadius: 999, border: 'none',
                  background: loading ? 'var(--color-uv-soft)' : 'var(--color-uv)', color: '#ffffff',
                  fontFamily: DATA, fontSize: 15, fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <svg className="animate-spin" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    {t('login.verification')}
                  </span>
                ) : (
                  t(mode === 'login' ? 'login.entrer' : 'login.demanderCompte')
                )}
              </button>
            </form>
          </>
        )}

        <style>{`
          .login-grille { display: grid; grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr); }
          .login-couv { display: flex !important; flex-direction: column; justify-content: flex-start; min-height: 100vh; padding: 40px clamp(28px, 5vw, 72px) 56px !important; align-content: initial; }
          .login-form { display: flex; flex-direction: column; justify-content: center; padding: 48px clamp(24px, 5vw, 72px); max-width: 560px; width: 100%; box-sizing: border-box; }
          .login-logo-mobile { display: none; }
          .login-champ::placeholder { color: var(--text-tertiary); }
          .login-champ:focus { box-shadow: inset 0 0 0 2.5px var(--color-uv) !important; }
          @media (max-width: 860px) {
            .login-grille { grid-template-columns: 1fr; }
            .login-couv { display: none !important; }
            .login-form { justify-content: flex-start; padding-top: 32px; }
            .login-logo-mobile { display: block; margin-bottom: 36px; }
          }
        `}</style>
      </main>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div style={{ minHeight: '100vh', background: 'var(--color-surface)' }} />}>
      <LoginContent />
    </Suspense>
  );
}
